/** Category tree mutations (rename / move / delete / create) + bookmark
 *  recategorize. Internal action engine for the host-owned category model.
 *
 *  Recategorize, rename, move, and delete each offer a 10s "Undo" action toast
 *  backed by inverse mutations. The inverse plans are built by the pure helpers
 *  in ../undo-plans from a snapshot taken before the mutation fires. */

import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CategoryAction, CategoryMeta } from 'markcat-domain';
import { apiFetch, friendlyErrorMessage } from '../client';
import { BOOKMARKS_QUERY_KEY, type BookmarkListQueryData } from '../queries/useBookmarks';
import { CATEGORY_META_QUERY_KEY } from '../queries/useCategories';
import { useBulkUpdate, useBulkRestore } from '../mutations/useBulkOperations';
import {
  useRenameCategory, useMoveCategory, useDeleteCategory,
  type CategoryChangeResponse,
} from '../mutations/useMoveCategory';
import {
  buildRecategorizeUndoPlan, deriveRenameUndo, deriveMoveUndo,
  buildCategoryDeleteSnapshot, type CategoryDeleteSnapshot,
} from '../undo-plans';

export type NotifyFn = (
  message: string,
  kind: 'info' | 'success' | 'error',
  action?: { label: string; onClick: () => void },
) => void;

export function useCategoryActions(notify: NotifyFn) {
  const queryClient = useQueryClient();
  const bulkUpdate = useBulkUpdate();
  const bulkRestore = useBulkRestore();
  const renameCategoryMut = useRenameCategory();
  const moveCategoryMut = useMoveCategory();
  const deleteCategoryMut = useDeleteCategory();

  // Stable ref so the callbacks don't churn when the caller passes an inline notify.
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  // ---- Undo actions (fired from action-toast buttons) ----

  // Re-create captured category_meta rows (icon/color) after a delete undo.
  const restoreMeta = useCallback((meta: CategoryMeta[]) =>
    Promise.all(meta.map((m) =>
      apiFetch('/api/category-meta', {
        method: 'POST',
        body: JSON.stringify({ path: m.path, icon: m.icon ?? null, color: m.color ?? null }),
      }),
    )), []);

  const undoRecategorize = useCallback((groups: Array<[string, string[]]>) => {
    Promise.all(groups.map(([oldPath, ids]) =>
      bulkUpdate.mutateAsync({ ids, updates: { category_path: oldPath } }),
    ))
      .then(() => notifyRef.current('Move undone', 'success'))
      .catch(() => notifyRef.current('Undo failed', 'error'));
  }, [bulkUpdate]);

  const undoCategoryDelete = useCallback((snapshot: CategoryDeleteSnapshot, cascade: boolean) => {
    const rehome = cascade
      ? bulkRestore.mutateAsync(snapshot.ids)
      : Promise.all(snapshot.idGroups.map(([oldPath, ids]) =>
          bulkUpdate.mutateAsync({ ids, updates: { category_path: oldPath } })));
    rehome
      .then(() => restoreMeta(snapshot.meta))
      .then(() => {
        queryClient.invalidateQueries({ queryKey: CATEGORY_META_QUERY_KEY });
        notifyRef.current('Category restored', 'success');
      })
      .catch(() => notifyRef.current('Undo failed', 'error'));
  }, [bulkRestore, bulkUpdate, restoreMeta, queryClient]);

  // Undo a rename/move by firing its inverse. mutateAsync + promise handlers
  // (not mutate-scoped callbacks) so the confirm toast still fires if the
  // owning component unmounted during the 10s window. A server "success" that
  // touched zero rows means the path to invert no longer existed (changed or
  // removed inside the window) — report failure, not a phantom undo.
  const undoPathChange = useCallback(<TInput,>(
    mut: { mutateAsync: (input: TInput) => Promise<CategoryChangeResponse> },
    input: TInput,
    doneMsg: string,
  ) => {
    mut.mutateAsync(input)
      .then((res) => {
        if (res.bookmarks_updated + res.category_meta_updated > 0) {
          notifyRef.current(doneMsg, 'success');
        } else {
          notifyRef.current('Undo failed', 'error');
        }
      })
      .catch(() => notifyRef.current('Undo failed', 'error'));
  }, []);

  const handleCategoryAction = useCallback((action: CategoryAction) => {
    // Both move variants (reparent + move-to-root) hit the same endpoint and
    // share one inverse: move new_path back under old_path's parent.
    const moveWithUndo = (targetParent: string | null, changedMsg: string) => {
      moveCategoryMut.mutate(
        { path: action.path, targetParent },
        {
          onSuccess: (res) => {
            if (res.old_path === res.new_path) return;  // server no-oped same-path
            notifyRef.current(changedMsg, 'success', {
              label: 'Undo',
              onClick: () => undoPathChange(
                moveCategoryMut, deriveMoveUndo(res.old_path, res.new_path), 'Move undone'),
            });
          },
          onError: () => notifyRef.current('Move failed', 'error'),
        },
      );
    };

    switch (action.type) {
      case 'rename':
        renameCategoryMut.mutate(
          { path: action.path, newName: action.name },
          {
            onSuccess: (res) => notifyRef.current(`Renamed to "${action.name}"`, 'success', {
              label: 'Undo',
              onClick: () => undoPathChange(
                renameCategoryMut, deriveRenameUndo(res.old_path, res.new_path), 'Rename undone'),
            }),
            onError: () => notifyRef.current('Rename failed', 'error'),
          },
        );
        break;
      case 'move-root':
        moveWithUndo(null, `Moved "${action.name}" to root`);
        break;
      case 'move':
        moveWithUndo(action.targetParent ?? null, 'Category moved');
        break;
      case 'delete': {
        const cached = queryClient.getQueryData<BookmarkListQueryData>(BOOKMARKS_QUERY_KEY);
        const meta = queryClient.getQueryData<CategoryMeta[]>(CATEGORY_META_QUERY_KEY);
        const snapshot = buildCategoryDeleteSnapshot(action.path, cached?.items ?? [], meta);
        if (action.cascade) {
          deleteCategoryMut.mutate(
            { path: action.path, cascade: true },
            {
              onSuccess: (res) => {
                const msg = `Deleted "${action.name}" and ${res.bookmarks_deleted} bookmark${res.bookmarks_deleted === 1 ? '' : 's'}`;
                // Undo only when the cache saw every deleted row — otherwise the
                // restore would silently miss the rows outside the loaded window.
                if (res.bookmarks_deleted === snapshot.ids.length) {
                  notifyRef.current(msg, 'success', {
                    label: 'Undo',
                    onClick: () => undoCategoryDelete(snapshot, true),
                  });
                } else {
                  notifyRef.current(msg, 'success');
                }
              },
              onError: () => notifyRef.current('Delete failed', 'error'),
            },
          );
          break;
        }
        // Keep bookmarks: the server re-homes the WHOLE subtree to
        // Uncategorized and reports the count — the cache only holds the
        // loaded window, so a client-side re-home would miss unloaded rows
        // and let the category reappear when a later page loads.
        deleteCategoryMut.mutate(
          { path: action.path },
          {
            onSuccess: (res) => {
              const moved = res.bookmarks_rehomed;
              const msg = moved
                ? `Deleted "${action.name}" — moved ${moved} bookmark${moved === 1 ? '' : 's'} to Uncategorized`
                : `Deleted "${action.name}"`;
              // Undo only when the cache saw every re-homed row AND the meta —
              // the same honesty rule as the cascade branch above.
              if (snapshot.metaCaptured && moved === snapshot.ids.length) {
                notifyRef.current(msg, 'success', {
                  label: 'Undo',
                  onClick: () => undoCategoryDelete(snapshot, false),
                });
              } else {
                notifyRef.current(msg, 'success');
              }
            },
            onError: () => notifyRef.current('Delete failed', 'error'),
          },
        );
        break;
      }
      case 'create':
        apiFetch('/api/category-meta', {
          method: 'POST',
          body: JSON.stringify({ path: action.name, icon: null, color: null }),
        }).then(() => {
          notifyRef.current(`Created "${action.name}"`, 'success');
          queryClient.invalidateQueries({ queryKey: CATEGORY_META_QUERY_KEY });
        }).catch((err) => notifyRef.current(`Create failed: ${friendlyErrorMessage(err)}`, 'error'));
        break;
    }
  }, [renameCategoryMut, moveCategoryMut, deleteCategoryMut, queryClient, undoCategoryDelete, undoPathChange]);

  const handleRecategorize = useCallback((ids: string[], targetPath: string) => {
    const cached = queryClient.getQueryData<BookmarkListQueryData>(BOOKMARKS_QUERY_KEY);
    const plan = buildRecategorizeUndoPlan(ids, cached?.items ?? []);
    bulkUpdate.mutate(
      { ids, updates: { category_path: targetPath } },
      {
        onSuccess: () => {
          const msg = `Moved ${ids.length} bookmark${ids.length > 1 ? 's' : ''} to ${targetPath}`;
          // No undo when the cache missed an id (e.g. selected via server search
          // beyond the loaded window) — a partial re-home would be worse.
          if (plan.complete) {
            notifyRef.current(msg, 'success', {
              label: 'Undo',
              onClick: () => undoRecategorize(plan.groups),
            });
          } else {
            notifyRef.current(msg, 'success');
          }
        },
        onError: () => notifyRef.current('Move failed', 'error'),
      },
    );
  }, [bulkUpdate, queryClient, undoRecategorize]);

  return { handleCategoryAction, handleRecategorize };
}
